/**
 * RP4 — Outer-stage interactions binder extracted from `Scene3DStage.tsx`.
 *
 * Wraps the `installStageInteractions` call so the orchestrator only
 * passes refs / callbacks; the wiring (canvas lookup, fall-back
 * camera, synthetic-Escape dispatch on touch, B8 cursor snapshot
 * bridge) lives here.
 *
 * No behavior changes — pure code move from `Scene3DStage.tsx`.
 */

import * as THREE from "three";
import * as React from "react";

import {
  installStageInteractions,
  type OsnapBindings,
  type StageInteractionsHandle,
  type ViewportRedirectContext,
} from "../stageInteractions";
import type { ZoomPickingState } from "../cameraApiAdapter";
import type { RefinedEntityPicker } from "./refinedEntityPick";
import type { SpatialIndex } from "../types";
import type { OverlayState } from "../overlays";
import type { Scene3DStageProps } from "../Scene3DStageProps";
import { setCursorWorldSnapshot } from "../dynInput/cursorWorldSnapshot";

interface BindStageInteractionsArgs {
  overlayShell: HTMLDivElement;
  liveCameraRef: { current: THREE.Camera | null };
  viewportSizeRef: { current: { width: number; height: number } };
  invalidateRef: { current: (() => void) | null };
  setOverlayState: React.Dispatch<React.SetStateAction<OverlayState>>;
  interactionCbsRef: {
    current: {
      onCanvasPoint?: Scene3DStageProps["onCanvasPoint"];
      onCanvasHover?: Scene3DStageProps["onCanvasHover"];
      onCursorClientChange?: Scene3DStageProps["onCursorClientChange"];
      onRightClickEnter?: Scene3DStageProps["onRightClickEnter"];
      onContextMenu?: Scene3DStageProps["onContextMenu"];
      onPanComplete?: Scene3DStageProps["onPanComplete"];
      onPanSessionEnd?: Scene3DStageProps["onPanSessionEnd"];
    };
  };
  activeSpaceId: string;
  /**
   * Un-collapsed active-space id forwarded verbatim to
   * `installStageInteractions` as the snap/hover resolver's identity
   * `fallbackSpaceId`. In MSPACE_VIA_VP this is the layout id (NOT the collapsed
   * `"model"` carried by `activeSpaceId`) so that over the paper sheet outside
   * the active VP the resolver scopes the candidate filter to the layout's own
   * geometry. See `StageInteractionBindings.identityFallbackSpaceId`.
   */
  identityFallbackSpaceId: string;
  spatialIndexRef: { current: SpatialIndex | null };
  /**
   * Codex PR #784 round 4 F2 — the stage-owned global off / frozen layer set
   * (adopted by `SceneContent` as its `hiddenLayersRef`), forwarded so the
   * hover hit-test filters INSERT child geometry like the click path.
   */
  hiddenLayersRef?: { readonly current: ReadonlySet<string> };
  /**
   * Codex PR #784 round 7 P2 — the stage-owned outlet carrying the REFINED
   * entity picker `installStageClickHandlers` publishes (see
   * `scene/refinedEntityPick.ts`). Forwarded verbatim so `ZOOM Object` picks
   * through the same lift + child-distance refinement a click does instead of
   * the bbox-only `SpatialIndex.pick`. Optional; `current === null` (handlers
   * not installed, or a host that never wires one) keeps the documented
   * `SpatialIndex.pick` fallback.
   */
  refinedEntityPickRef?: { readonly current: RefinedEntityPicker | null };
  zoomPickingRef: { current: ZoomPickingState | null };
  osnapBindingsRef: { current: OsnapBindings | null };
  stageCameraApiRefProp: Scene3DStageProps["stageCameraApiRef"];
  // cmd-pan — interactive realtime-pan session flag + end callback (bare PAN /
  // right-click 'Pan'). When `panSessionActiveRef.current` is true a LMB drag
  // pans the camera; `onPanSessionEnd` fires on Esc/Enter to clear the session.
  panSessionActiveRef: { current: boolean };
  onPanSessionEnd: () => void;
  // MC4-interactive Slice 3 — per-VP zoom/pan redirect.
  viewportRedirectRef: { current: ViewportRedirectContext | null };
  onViewportSetView: (
    viewportId: string,
    view: { viewCenter?: { x: number; y: number }; viewHeight?: number },
  ) => void;
  /**
   * MC4 — live "draft in progress" flag (edge case #2). Forwarded verbatim to
   * `installStageInteractions.draftActiveRef`; when `current === true` the
   * pointer-move resolver runs ungated so the cursor maps into the active VP
   * MODEL space past the VP edge (rubber band spans the frame, clip trims it).
   */
  draftActiveRef: { current: boolean };
  /**
   * OTRACK-SUBSYSTEM — "the current draft step is an active point prompt"
   * flag. Forwarded verbatim to
   * `installStageInteractions.draftPointPromptActiveRef`; gates hover-dwell
   * OTRACK acquisition so idle hovering / a non-point sub-prompt never
   * leaves stale acquired points.
   */
  draftPointPromptActiveRef: { current: boolean };
  /**
   * OTRACK-SUBSYSTEM (local Codex review round 6 P2) — "draft generation"
   * counter, bumped whenever the underlying command or its committed point
   * count changes. Forwarded verbatim to
   * `installStageInteractions.draftGenerationRef`; lets a pending hover-dwell
   * timer detect that the command it armed under has ended, even if a
   * DIFFERENT command's point prompt is active again by the time it fires.
   */
  draftGenerationRef: { current: number };
  /**
   * ORTHO-constraint (AutoCAD ORTHOMODE / F8) — the draft rubber-band anchor
   * ($LASTPOINT) in the pointer-resolved space. Forwarded verbatim to
   * `installStageInteractions.orthoBasePointRef`; when non-null + ortho effective
   * + no OSNAP hit the emitted cursor is locked to 0/90° from it. Stable ref,
   * `current` refreshed each render by the host from the live draft session.
   */
  orthoBasePointRef: { current: { x: number; y: number } | null };
  /**
   * N6 (POLAR-RELATIVE-BASIS-WIRE) — direction (degrees) of the last segment
   * drawn. Forwarded verbatim to `installStageInteractions
   * .polarReferenceAngleRef`; `null` when there is no prior segment.
   */
  polarReferenceAngleRef: { current: number | null };
}

/**
 * Build the `installStageInteractions` arg bundle and install. The
 * caller's effect must guard for `overlayShellRef.current === null` and
 * a missing `<canvas>` child before invoking.
 *
 * Returns the full {@link StageInteractionsHandle} (NOT a bare cleanup): the
 * host needs `refreshSnapAtLastPointer` to re-resolve the snap when the
 * effective OSNAP bitmask changes with no pointer move, and calls `dispose` for
 * the effect cleanup.
 */
export function bindStageInteractions(
  args: BindStageInteractionsArgs,
): StageInteractionsHandle | undefined {
  const canvas = args.overlayShell.querySelector(
    "canvas",
  ) as HTMLCanvasElement | null;
  if (!canvas) return undefined;
  return installStageInteractions({
    canvas,
    overlayContainer: args.overlayShell,
    cameraRef: {
      get current(): THREE.Camera {
        // Fall back to a dummy camera if R3F hasn't populated yet.
        return args.liveCameraRef.current ?? new THREE.Camera();
      },
    },
    sizeRef: args.viewportSizeRef,
    invalidate: () => {
      args.invalidateRef.current?.();
    },
    setOverlayState: args.setOverlayState,
    onCanvasPoint: (point, spaceId) =>
      args.interactionCbsRef.current.onCanvasPoint?.(point, spaceId),
    onCanvasHover: (point, spaceId, entityId) => {
      // B8 — bridge live cursor world coord into the module-level
      // snapshot consumed by `<DynInputOverlay>` (sibling DOM layer).
      setCursorWorldSnapshot(point);
      args.interactionCbsRef.current.onCanvasHover?.(point, spaceId, entityId);
    },
    onCursorClientChange: (cursor) =>
      args.interactionCbsRef.current.onCursorClientChange?.(cursor),
    onRightClickEnter: () =>
      args.interactionCbsRef.current.onRightClickEnter?.(),
    onContextMenu: (screen, shiftKey) =>
      args.interactionCbsRef.current.onContextMenu?.(screen, shiftKey),
    onPanComplete: (delta) =>
      args.interactionCbsRef.current.onPanComplete?.(delta),
    // cmd-pan P2-2/P2-3 — on a CONFIRMED non-zero completion of an interactive
    // PAN drag, push the PRE-DRAG view (captured at drag start, passed here as
    // `state`) onto the ZOOM-Previous ring via the camera adapter. Passing the
    // explicit pre-drag snapshot (rather than letting the adapter read the live
    // camera) is load-bearing: at completion the live camera has already moved.
    // A bare PAN exited without a drag, a zero-NET drag, or a VP-redirected drag
    // never fires this, so the ring is never polluted with a no-op entry.
    onPanSnapshotView: (state) => {
      args.stageCameraApiRefProp?.current?.snapshotView(state);
    },
    // cmd-pan r5 P2-3 — at LMB pan-drag START, cancel any in-flight camera tween
    // (just-issued ZOOM / -PAN / layout auto-fit) so the drag owns the camera and
    // the per-frame CameraApiBinder stops re-applying the stale tween target.
    onCancelPendingTween: () => {
      args.stageCameraApiRefProp?.current?.clearPendingTween();
    },
    activeSpaceId: args.activeSpaceId,
    identityFallbackSpaceId: args.identityFallbackSpaceId,
    spatialIndexRef: args.spatialIndexRef,
    refinedEntityPickRef: args.refinedEntityPickRef,
    hiddenLayersRef: args.hiddenLayersRef,
    zoomPickingRef: args.zoomPickingRef,
    osnapBindingsRef: args.osnapBindingsRef,
    onTouchZoomExtents: () => {
      const api = args.stageCameraApiRefProp?.current;
      api?.zoomExtents();
    },
    onTouchEscape: () => {
      // Dispatch a synthetic Escape keydown so the global keydown
      // listener (and any feature-specific listeners) receive a single
      // shared signal.
      window.dispatchEvent(
        new KeyboardEvent("keydown", { key: "Escape", bubbles: true }),
      );
    },
    // MC4-interactive Slice 3 — per-VP zoom/pan redirect.
    viewportRedirectRef: args.viewportRedirectRef,
    onViewportSetView: args.onViewportSetView,
    // MC4 — draft routing past the active VP edge (edge case #2).
    draftActiveRef: args.draftActiveRef,
    // OTRACK-SUBSYSTEM — "active point prompt" gate for hover-dwell acquisition.
    draftPointPromptActiveRef: args.draftPointPromptActiveRef,
    // OTRACK-SUBSYSTEM (local Codex review round 6 P2) — draft generation
    // counter, so a pending dwell timer can detect a command boundary crossed.
    draftGenerationRef: args.draftGenerationRef,
    // ORTHO-constraint (AutoCAD ORTHOMODE / F8) — draft anchor for the 0/90° lock.
    orthoBasePointRef: args.orthoBasePointRef,
    // N6 — POLAR relative-basis reference angle (last-segment direction).
    polarReferenceAngleRef: args.polarReferenceAngleRef,
    // cmd-pan — interactive realtime-pan session (bare PAN / right-click 'Pan').
    panSessionActiveRef: args.panSessionActiveRef,
    onPanSessionEnd: () => args.onPanSessionEnd(),
  });
}
